import React from 'react';
import { observer } from 'mobx-react';
import { Loader, BaasicInput, DatePicker, BaasicDropdown, AddressAutofill } from 'core/components';
import { isShippingStateAvailable } from 'core/utils';

const MarketplaceOrderEditOrderStep = observer(
	({
		t,
		form,
		viewStore: {
			loading,
			datePickerFormat,
			sellerDropdownStore,
			isSuperAdmin,
			isEdit,
			orderStatusDropdownStore,
			orderTypeDropdownStore,
			channelDropdownStore,
		},
	}) => {
		if (loading) return <Loader />;
		return (
			<section>
				<h3 className="spc--bottom--med title--primary">{t('MARKETPLACEORDEREDITFORM.TITLE.ORDERSTEP')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-lrg-12 col-xxlrg-8">
						<div className="spc--bottom--sml">
							{isSuperAdmin && (
								<BaasicDropdown
									dropdownStore={sellerDropdownStore}
									placeholder={t('PLACEHOLDER.SELLER')}
									className="w--200--px spc--right--sml"
									isDisabled={isEdit}
								/>
							)}
						</div>
						<div className="row">
							<div className="col col-sml-6">
								<BaasicInput autoFocus field={form.$('orderNumber')} />
								<div className="spc--bottom--sml">
									<DatePicker field={form.$('orderDate')} format={datePickerFormat} />
								</div>
								<div className="spc--bottom--sml">
									<DatePicker field={form.$('shipByDate')} format={datePickerFormat} />
								</div>
								<div className="spc--bottom--sml">
									<DatePicker field={form.$('paymentDate')} format={datePickerFormat} />
								</div>
								<div className="spc--bottom--sml">
									<DatePicker field={form.$('deliverByDate')} format={datePickerFormat} />
								</div>
								<BaasicDropdown dropdownStore={orderTypeDropdownStore} />
							</div>
							<div className="col col-sml-6">
								<BaasicInput field={form.$('internalNote')} />
								<BaasicInput field={form.$('ref1')} />
								<BaasicInput field={form.$('ref2')} />
								<BaasicInput field={form.$('ref3')} />
								{channelDropdownStore && channelDropdownStore.options.length > 1 && (
									<BaasicDropdown dropdownStore={channelDropdownStore} className="spc--bottom--sml flex--grow--1" />
								)}
								<BaasicDropdown dropdownStore={orderStatusDropdownStore} className="spc--bottom--sml flex--grow--1" />
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}
);

const MarketplaceOrderEditShippingStep = observer(
	({
		t,
		form,
		viewStore: {
			loading,
			addressAutofillViewStore,
			confirmationTypeDropdownStore,
			currencyDropdownStore,
			countryDropdownStore,
			stateDropdownStore,
		},
	}) => {
		if (loading) return <Loader />;
		return (
			<section>
				<h3 className="spc--bottom--med title--primary">{t('MARKETPLACEORDEREDITFORM.TITLE.SHIPPINGSTEP')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-xxlrg-8">
						<div className="row">
							<div className="col col-sml-6">
								<AddressAutofill viewStore={addressAutofillViewStore} t={t} />
								<BaasicInput field={form.$('shippingAddress2')} className="fs-mask" />
								<BaasicInput field={form.$('shippingAddress3')} className="fs-mask" />
								<BaasicInput field={form.$('shippingCity')} />
								<BaasicInput field={form.$('shippingZipCode')} />
								<BaasicInput field={form.$('buyerName')} className="fs-mask" />
								<BaasicInput field={form.$('buyerPhone')} className="fs-mask" />
							</div>
							<div className="col col-sml-6">
								<BaasicDropdown dropdownStore={countryDropdownStore} className="spc--bottom--sml flex--grow--1" />
								<BaasicDropdown
									dropdownStore={stateDropdownStore}
									isDisabled={
										countryDropdownStore &&
										countryDropdownStore.value &&
										!isShippingStateAvailable(countryDropdownStore.value.extra.countryCode)
									}
									className="spc--bottom--sml flex--grow--1"
								/>
								<BaasicDropdown
									dropdownStore={confirmationTypeDropdownStore}
									className="spc--bottom--sml flex--grow--1"
								/>
								<BaasicInput field={form.$('shippingPaid')} />
								<BaasicDropdown dropdownStore={currencyDropdownStore} className="spc--bottom--sml flex--grow--1" />
								<BaasicInput field={form.$('buyerEmail')} />
								<BaasicInput field={form.$('customerNote')} />
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}
);

const MarketplaceOrderEditOrderLinesStep = observer(
	({ t, form, viewStore: { loading, addOrderLine, onOrderLineDelete } }) => {
		if (loading) return <Loader />;
		return (
			<section>
				<div className="row">
					<div className="col col-sml-8">
						<div className="flex--secondary">
							<h3 className="spc--bottom--med title--primary">{t('MARKETPLACEORDEREDITFORM.TITLE.ORDERLINESSTEP')}</h3>
							<button className="btn btn--med btn--secondary spc--bottom--sml" onClick={addOrderLine} type="button">
								<i className="fa fa-plus spc--right--sml"></i> {t('MARKETPLACEORDEREDITFORM.ADDORDERLINE')}
							</button>
						</div>
						{form.$('orderLines').map(orderLine => {
							return (
								<div className="box--secondary box--sml spc--bottom--tny" key={orderLine.key}>
									{orderLine.key != 0 && (
										<div className="row">
											<div className="col col-sml-12">
												<div className="inputgroup type--right">
													<button
														className="btn btn--med btn--ghost react-filter-box-close"
														onClick={e => onOrderLineDelete(e, orderLine)}
														type="button"
													>
														<i className="fa fa-times"></i>
													</button>
												</div>
											</div>
										</div>
									)}
									<div className="row">
										<div className="col col-sml-3 col-lrg-3 col-xxlrg-3">
											<BaasicDropdown dropdownStore={orderLine.productDropdownStore} />
										</div>
										<div className="col col-sml-3 col-lrg-3 col-xxlrg-3">
											<BaasicInput field={orderLine.$('quantity')} />
										</div>
										<div className="col col-sml-3 col-lrg-2 col-xxlrg-2">
											<BaasicInput field={orderLine.$('unitPrice')} />
										</div>
										<div className="col col-sml-2 col-lrg-2 col-xxlrg-2">
											<BaasicInput field={orderLine.$('taxPaid')} />
										</div>
										<div className="col col-sml-2 col-lrg-2 col-xxlrg-2">
											<BaasicDropdown dropdownStore={orderLine.orderLineStatusDropdownStore} />
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</section>
		);
	}
);

const MarketplaceOrderEditAssignmentStep = observer(
	({
		t,
		viewStore: {
			loading,
			requiredWarehouseDropdownStore,
			requiredCarrierServiceDropdownStore,
			everyProductIsNonShippable,
		},
	}) => {
		if (loading) return <Loader />;
		return (
			<section>
				<h3 className="spc--bottom--med title--primary">{t('MARKETPLACEORDEREDITFORM.TITLE.ASSIGNMENTSTEP')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-lrg-12 col-xxlrg-8">
						<div className="row">
							<div className="col col-sml-6">
								<BaasicDropdown
									dropdownStore={requiredWarehouseDropdownStore}
									isDisabled={everyProductIsNonShippable}
									isClearable
								/>
							</div>
							<div className="col col-sml-6">
								<BaasicDropdown
									dropdownStore={requiredCarrierServiceDropdownStore}
									isDisabled={everyProductIsNonShippable}
									isClearable
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}
);

export {
	MarketplaceOrderEditOrderStep,
	MarketplaceOrderEditShippingStep,
	MarketplaceOrderEditOrderLinesStep,
	MarketplaceOrderEditAssignmentStep,
};



// WEBPACK FOOTER //
// ./src/modules/order/pages/wizard/MarketplaceOrderEditSteps.jsx